import React, { useState, useRef, useEffect } from 'react';
import '../../../../styles/resume-editor.css';

export type PageSize = 'letter' | 'a4' | 'legal';

export interface Margins {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface PageSizeMarginsProps {
  pageSize?: PageSize;
  margins?: Margins;
  onPageSizeChange?: (pageSize: PageSize) => void;
  onMarginsChange?: (margins: Margins) => void;
  onLayoutChange?: (layout: string) => void;
  initialLayout?: string;
}

interface PageSizeOption {
  id: string;
  name: string;
  dimensions: string;
}

export const PageSizeMargins: React.FC<PageSizeMarginsProps> = ({
  pageSize: externalPageSize,
  margins: externalMargins,
  onPageSizeChange,
  onMarginsChange,
  onLayoutChange,
  initialLayout,
}) => {
  const [pageSize, setPageSize] = useState<PageSize>(externalPageSize || 'letter');
  const [margins, setMargins] = useState<Margins>(
    externalMargins || {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5,
    }
  );
  
  const getInitialLayout = (): 'inline' | 'stacked' => {
    if (initialLayout) {
      const layoutMap: Record<string, 'inline' | 'stacked'> = {
        'Inline': 'inline',
        'Stacked': 'stacked',
      };
      return layoutMap[initialLayout] || 'inline';
    }
    return 'inline';
  };
  
  const [certificationLayout, setCertificationLayout] = useState<'inline' | 'stacked'>(getInitialLayout());
  const prevLayoutRef = useRef<'inline' | 'stacked'>(getInitialLayout());

  useEffect(() => {
    if (initialLayout) {
      const layoutMap: Record<string, 'inline' | 'stacked'> = {
        'Inline': 'inline',
        'Stacked': 'stacked',
      };
      const frontendLayout = layoutMap[initialLayout] || 'inline';
      setCertificationLayout(frontendLayout);
      prevLayoutRef.current = frontendLayout;
    }
  }, [initialLayout]);
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = useRef<HTMLDivElement>(null);

  const pageSizeOptions: PageSizeOption[] = [
    { id: 'letter', name: 'Letter', dimensions: '8.5 x 11 inches' },
    { id: 'a4', name: 'A4', dimensions: '8.27 x 11.69 inches' },
    { id: 'legal', name: 'Legal', dimensions: '8.5 x 14 inches' },
  ];

  const selectedPageSize = pageSizeOptions.find((size) => size.id === pageSize) || pageSizeOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target as Node)) {
        setPageSizeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (externalPageSize) {
      setPageSize(externalPageSize);
    }
  }, [externalPageSize]);

  useEffect(() => {
    if (externalMargins) {
      setMargins(externalMargins);
    }
  }, [externalMargins]);

  const handlePageSizeChange = (newPageSize: string) => {
    const size = newPageSize as PageSize;
    setPageSize(size);
    onPageSizeChange?.(size);
  };

  const handleMarginChange = (field: 'left' | 'right' | 'top' | 'bottom', delta: number) => {
    setMargins((prev) => {
      const newValue = Math.max(0, Math.min(2, prev[field] + delta));
      const updated = { ...prev, [field]: Math.round(newValue * 100) / 100 };
      onMarginsChange?.(updated);
      return updated;
    });
  };

  const handleMarginInputChange = (field: 'left' | 'right' | 'top' | 'bottom', value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const clamped = Math.max(0, Math.min(2, numValue));
      setMargins((prev) => {
        const updated = {
          ...prev,
          [field]: Math.round(clamped * 100) / 100,
        };
        onMarginsChange?.(updated);
        return updated;
      });
    }
  };

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="page-size-margins__section">
        <label className="page-size-margins__label">Page Size</label>
        <div className="page-size-margins__dropdown" ref={pageSizeDropdownRef}>
          <button
            type="button"
            className="page-size-margins__dropdown-trigger"
            onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
          >
            <span className="page-size-margins__dropdown-value">
              {selectedPageSize.name} {selectedPageSize.dimensions}
            </span>
            <span className="page-size-margins__dropdown-arrow">
              {pageSizeDropdownOpen ? '▴' : '▾'}
            </span>
          </button>
          {pageSizeDropdownOpen && (
            <div className="page-size-margins__dropdown-menu">
              {pageSizeOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`page-size-margins__dropdown-item ${pageSize === option.id ? 'page-size-margins__dropdown-item--selected' : ''}`}
                  onClick={() => {
                    handlePageSizeChange(option.id);
                    setPageSizeDropdownOpen(false);
                  }}
                >
                  {option.name} {option.dimensions}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-size-margins__section">
        <label className="page-size-margins__label">Margins (in inches)</label>
        <div className="page-size-margins__margins-grid">
          <div className="page-size-margins__margin-field">
            <label className="page-size-margins__margin-label">Left</label>
            <div className="page-size-margins__number-input">
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('left', -0.1)}
              >
                −
              </button>
              <input
                type="number"
                className="page-size-margins__number-input-field"
                value={margins.left}
                onChange={(e) => handleMarginInputChange('left', e.target.value)}
                step={0.1}
                min={0}
                max={2}
              />
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('left', 0.1)}
              >
                +
              </button>
            </div>
          </div>

          <div className="page-size-margins__margin-field">
            <label className="page-size-margins__margin-label">Right</label>
            <div className="page-size-margins__number-input">
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('right', -0.1)}
              >
                −
              </button>
              <input
                type="number"
                className="page-size-margins__number-input-field"
                value={margins.right}
                onChange={(e) => handleMarginInputChange('right', e.target.value)}
                step={0.1}
                min={0}
                max={2}
              />
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('right', 0.1)}
              >
                +
              </button>
            </div>
          </div>

          <div className="page-size-margins__margin-field">
            <label className="page-size-margins__margin-label">Top</label>
            <div className="page-size-margins__number-input">
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('top', -0.1)}
              >
                −
              </button>
              <input
                type="number"
                className="page-size-margins__number-input-field"
                value={margins.top}
                onChange={(e) => handleMarginInputChange('top', e.target.value)}
                step={0.1}
                min={0}
                max={2}
              />
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('top', 0.1)}
              >
                +
              </button>
            </div>
          </div>

          <div className="page-size-margins__margin-field">
            <label className="page-size-margins__margin-label">Bottom</label>
            <div className="page-size-margins__number-input">
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('bottom', -0.1)}
              >
                −
              </button>
              <input
                type="number"
                className="page-size-margins__number-input-field"
                value={margins.bottom}
                onChange={(e) => handleMarginInputChange('bottom', e.target.value)}
                step={0.1}
                min={0}
                max={2}
              />
              <button
                className="page-size-margins__number-btn"
                onClick={() => handleMarginChange('bottom', 0.1)}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-size-margins__section">
        <label className="page-size-margins__label">Certification Layout</label>
        <div className="page-size-margins__cert-layouts">
          <div
            className={`page-size-margins__cert-layout-card ${certificationLayout === 'inline' ? 'page-size-margins__cert-layout-card--selected' : ''}`}
            onClick={() => {
              if (certificationLayout !== 'inline') {
                setCertificationLayout('inline');
                prevLayoutRef.current = 'inline';
                onLayoutChange?.('Inline');
              }
            }}
          >
            <div className="page-size-margins__cert-layout-header">
              <span className="page-size-margins__cert-layout-name">Inline</span>
              <span className="page-size-margins__cert-layout-tag">Compact</span>
            </div>
            <div className="page-size-margins__cert-layout-content">
              <div className="page-size-margins__cert-layout-row">
                <span className="page-size-margins__cert-layout-text">Certification Name - Organization - ID</span>
                <span className="page-size-margins__cert-layout-date">01/2025</span>
              </div>
              <div className="page-size-margins__cert-layout-description">Description</div>
            </div>
          </div>

          <div
            className={`page-size-margins__cert-layout-card ${certificationLayout === 'stacked' ? 'page-size-margins__cert-layout-card--selected' : ''}`}
            onClick={() => {
              if (certificationLayout !== 'stacked') {
                setCertificationLayout('stacked');
                prevLayoutRef.current = 'stacked';
                onLayoutChange?.('Stacked');
              }
            }}
          >
            <div className="page-size-margins__cert-layout-header">
              <span className="page-size-margins__cert-layout-name">Stacked</span>
              <span className="page-size-margins__cert-layout-tag">Expanded</span>
            </div>
            <div className="page-size-margins__cert-layout-content">
              <div className="page-size-margins__cert-layout-row">
                <span className="page-size-margins__cert-layout-text">Certification Name</span>
                <span className="page-size-margins__cert-layout-date">01/2025</span>
              </div>
              <div className="page-size-margins__cert-layout-text">Organization | ID</div>
              <div className="page-size-margins__cert-layout-description">Description</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

